import { ApolloClient } from "@apollo/client";
import { DocumentNode } from "graphql";
import { GraphQLPageable, GraphQLPageableQueryResult } from "../types";
import { extractPageableResult } from "../utils";
import { GraphQLLoaderOptions, GraphQLLoaderProgressParams, GraphQLLoaderQueryVariables } from "./types";

export const baseLoaderOptions: GraphQLLoaderOptions = {
  onProgress: e => console.log(JSON.stringify(e)),
  pageSize: 100,
  batchSize: 15,
  extractQueryData: extractPageableResult,
  collectItems: true,
};

export async function loadData<T>(query: DocumentNode, options?: GraphQLLoaderOptions<T>): Promise<T[]> {
  const client = options?.client as ApolloClient<any>;
  const extractQueryData = options?.extractQueryData || baseLoaderOptions.extractQueryData as (data: GraphQLPageableQueryResult<T>) => GraphQLPageable<T>;
  const pageSize = options?.pageSize || baseLoaderOptions.pageSize as number;
  const batchSize = options?.batchSize || baseLoaderOptions.batchSize as number;
  const onProgress = options?.onProgress || baseLoaderOptions.onProgress as (e: GraphQLLoaderProgressParams) => any;
  const collectItems = options?.collectItems || baseLoaderOptions.collectItems;

  const { totalElements, totalPages, items } = extractQueryData((await client.query<GraphQLPageableQueryResult<T>, GraphQLLoaderQueryVariables>({
    query,
    variables: {
      page: 0,
      size: pageSize,
      orders: options?.orders,
      filter: options?.filter,
      ...options?.queryAdditionalProps,
    },
  })).data);

  const progress: GraphQLLoaderProgressParams = {
    totalItems: totalElements,
    loadedItems: items.length,
    progress: items.length / totalElements,
  };
  const result = [] as T[];
  onProgress(progress);
  if (collectItems) {
    result.push(...items);
  }
  await options?.processItems?.(items, { totalItems: totalElements });


  if (totalPages !== 1) {
    let page = 1;

    for (let batchIterator = 0; batchIterator < Math.ceil(totalElements / batchSize / pageSize); batchIterator++) {
      onProgress(progress);
      const tasks = [] as Promise<T[]>[];
      for (let taskIterator = 0; taskIterator < batchSize; taskIterator++) {
        const currentPage = page++;
        tasks.push(new Promise<T[]>(async (resolve, reject) => {
          if (currentPage * pageSize < totalElements) {
            await new Promise(resolve => setTimeout(resolve, 0));
            const data = extractQueryData((await client.query<GraphQLPageableQueryResult<T>, GraphQLLoaderQueryVariables>({
              query,
              variables: {
                page: currentPage,
                size: pageSize,
                orders: options?.orders,
                filter: options?.filter,
                ...options?.queryAdditionalProps,
              },
            })).data);

            try {
              await options?.processItems?.(data?.items, { totalItems: totalElements });
            } catch (err) {
              reject(err);
            }

            progress.loadedItems += data.items.length;
            progress.progress = progress.loadedItems / progress.totalItems;
            onProgress(progress);

            resolve(data.items);
          } else {
            resolve([]);
          }
        }));
      }
      const taskResults = await Promise.all(tasks);
      if (collectItems) {
        taskResults.forEach(taskResults => result.push(...taskResults));
      }
    }
  }

  return result;
}

